import { useRef, useState } from "react"
import {
  ArrowCounterClockwise,
  Archive,
  CalendarBlank,
  Copy,
  DotsThree,
  FolderPlus,
  Package,
  PencilSimple,
  Trash,
} from "@phosphor-icons/react"

import {
  Badge,
  Button,
  DateRangePicker,
  EmptyState,
  ExpandableCard,
  ExpandableCardContent,
  ExpandableCardHeader,
  IconButton,
  InlineAlert,
  Input,
  PermissionTooltip,
} from "~/components/design-system/components"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/reusable/DropdownMenu"
import { usePermission } from "~/hooks/usePermission"
import { FilterScopeProvider } from "~/providers/FilterScopeProvider"
import { api } from "~/utils/api"
import { DateFormatType, formatDate, toUTCDateString } from "~/utils/datetime"
import { pluralize } from "~/utils/formatting"
import { PACKAGE_NAME_MAX, packageName } from "~/utils/allocationPackageOps"
import { showErrorToast } from "~/utils/toaster"
import { CardTitle, PACKAGE_CARD_KEY } from "./PackageCardTitle"
import { PACKAGE_STATUS_META, packageWindowLabel } from "./packageDisplay"
import { STORES_SCOPE, StoresCardBody, StoresSource } from "./PackageStoresCard"
import { noteRunAfterCurrent, usePackageStoresDraft } from "./setup/setupDrafts"
import type { PackageFolder, RangePackage } from "./types"
import { useAllocationMutations } from "./useAllocationData"

type SaveFolder = ReturnType<typeof useAllocationMutations>["savePackageFolder"]

const FOLDER_NOT_ACTIVE_REASON = "This folder is archived, so it can't be changed."
const FOLDER_DELETING_REASON = "This folder is marked for deletion. Keep it to change it."

function folderDisabledReason(folder: PackageFolder, deleting: boolean) {
  if (deleting) return FOLDER_DELETING_REASON
  return folder.deactivated ? FOLDER_NOT_ACTIVE_REASON : undefined
}
const toDate = (day: string) => new Date(`${day}T00:00:00Z`)
const dateFieldLabel = (date: Date) => formatDate(date, DateFormatType.FullDateShort)

/** "New folder", or "New folder 2" and on when a sibling already carries it. */
function freeFolderName(base: string, siblings: { name: string }[]) {
  const taken = new Set(siblings.map((folder) => folder.name.trim().toLowerCase()))
  if (!taken.has(base.toLowerCase())) return base
  let n = 2
  while (taken.has(`${base} ${n}`.toLowerCase())) n++
  return `${base} ${n}`
}

/** The folder and its parent, outermost first. */
export function folderPath(folders: PackageFolder[], folderId: string | null) {
  const byId = new Map(folders.map((folder) => [folder.id, folder]))
  const folder = folderId === null ? undefined : byId.get(folderId)
  if (!folder) return []
  const parent = folder.parentId === null ? undefined : byId.get(folder.parentId)
  return parent ? [parent, folder] : [folder]
}

/**
 * The impact view before a delete is staged: what goes with it, then Mark for
 * deletion. Nothing is deleted until the Commandbar saves.
 */
export function DeletionImpact({
  target,
  label,
  onCancel,
  onMarked,
}: {
  target: { packageId?: string; folderId?: string }
  label: string
  onCancel: () => void
  onMarked: () => void
}) {
  const draft = usePackageStoresDraft()
  const impact = api.allocation.getPackageDeletionImpact.useQuery({
    packageIds: target.packageId ? [target.packageId] : [],
    folderIds: target.folderId ? [target.folderId] : [],
  })
  const data = impact.data
  const lines = data
    ? [
        target.folderId ? pluralize(data.packageCount, "package") : null,
        `${pluralize(data.variantCount, "variant")} · ${pluralize(data.skuCount, "SKU")}`,
        `${pluralize(data.storeCount, "store")} stop getting ${target.folderId ? "them" : "it"}`,
        `${pluralize(data.lineCount, "replenishment line")} (${pluralize(data.lineUnits, "unit")}) stop being suggested`,
        data.cartLineCount > 0
          ? `${pluralize(data.cartLineCount, "cart line")} lose their line`
          : null,
      ].filter((line): line is string => line !== null)
    : []
  return (
    <InlineAlert
      tone="destructive"
      size="block"
      title={`Delete ${label}?`}
      description={
        impact.isLoading ? (
          "Counting what goes with it…"
        ) : (
          <div className="flex flex-col gap-1">
            <span>Saving the changes removes:</span>
            <ul className="list-disc pl-5">
              {lines.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
            <span>{"It's marked for deletion first; Discard in the bar below keeps it."}</span>
          </div>
        )
      }
      secondaryAction={{ label: "Cancel", onClick: onCancel }}
      action={{
        label: "Mark for deletion",
        onClick: () => {
          draft.markForDeletion(target)
          onMarked()
        },
        requires: "edit",
        disabled: impact.isLoading || draft.saving,
      }}
    />
  )
}

function folderNameError(value: string, taken: Set<string>) {
  const parsed = packageName.safeParse(value)
  if (!parsed.success) return parsed.error.issues[0]?.message.replace("package", "folder")
  if (taken.has(value.trim().toLowerCase())) return "Another folder here already carries this name."
  return undefined
}

function FolderName({
  folder,
  siblings,
  nameRef,
  disabledReason,
  save,
}: {
  folder: PackageFolder
  siblings: PackageFolder[]
  nameRef: React.RefObject<HTMLInputElement | null>
  disabledReason: string | undefined
  save: SaveFolder
}) {
  const [value, setValue] = useState(folder.name)
  const { allowed } = usePermission("edit")
  const taken = new Set(
    siblings
      .filter((other) => other.id !== folder.id)
      .map((other) => other.name.trim().toLowerCase()),
  )
  const error = folderNameError(value, taken)
  const commit = () => {
    if (value.trim() === folder.name.trim()) return setValue(folder.name)
    if (error) return
    save.mutate(
      { op: { kind: "renameFolder", folderId: folder.id, name: value.trim() } },
      { onError: showErrorToast },
    )
  }
  return (
    <PermissionTooltip requires="edit" allowedTitle={disabledReason}>
      <div className="w-[320px]">
        <Input
          ref={nameRef}
          aria-label="Folder name"
          value={value}
          maxLength={PACKAGE_NAME_MAX}
          onChange={(event) => setValue(event.target.value)}
          onBlur={commit}
          onKeyDown={(event) => {
            if (event.key === "Enter") event.currentTarget.blur()
            if (event.key === "Escape") setValue(folder.name)
          }}
          disabled={!allowed || disabledReason !== undefined || save.isPending}
          className="heading-sm"
        />
        {error && value !== folder.name && (
          <p className="paragraph-xs mt-1 text-fg-negative-text">{error}</p>
        )}
      </div>
    </PermissionTooltip>
  )
}

function FolderActionsMenu({
  folder,
  folders,
  nameRef,
  disabledReason,
  save,
  deleting,
  onSelectFolder,
  onDelete,
}: {
  folder: PackageFolder
  folders: PackageFolder[]
  nameRef: React.RefObject<HTMLInputElement | null>
  disabledReason: string | undefined
  save: SaveFolder
  deleting: boolean
  onSelectFolder: (folderId: string) => void
  onDelete: () => void
}) {
  const draft = usePackageStoresDraft()
  const newSubfolder = () => {
    const folderId = crypto.randomUUID()
    save.mutate(
      {
        op: {
          kind: "createFolder",
          folderId,
          parentId: folder.id,
          window: null,
          name: freeFolderName(
            "New subfolder",
            folders.filter((other) => other.parentId === folder.id),
          ),
        },
      },
      { onSuccess: () => onSelectFolder(folderId), onError: showErrorToast },
    )
  }
  const duplicate = () => {
    const folderId = crypto.randomUUID()
    save.mutate(
      {
        op: {
          kind: "duplicateFolder",
          folderId,
          sourceId: folder.id,
          name: freeFolderName(
            `${folder.name.trim()} (copy)`,
            folders.filter((other) => other.parentId === folder.parentId),
          ),
        },
      },
      {
        onSuccess: (result) => {
          noteRunAfterCurrent(result.run)
          onSelectFolder(folderId)
        },
        onError: showErrorToast,
      },
    )
  }
  const setActive = (active: boolean) =>
    save.mutate(
      { op: { kind: "setFolderActive", folderId: folder.id, active } },
      { onSuccess: (result) => noteRunAfterCurrent(result.run), onError: showErrorToast },
    )
  const busy = save.isPending || draft.saving
  return (
    <DropdownMenu>
      <DropdownMenuTrigger requires="edit" allowedTitle="More actions" asChild>
        <IconButton variant="transparent" size="sm" aria-label="More actions">
          <DotsThree size={16} weight="bold" />
        </IconButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem
          disabled={disabledReason !== undefined}
          onSelect={() =>
            requestAnimationFrame(() => {
              nameRef.current?.focus()
              nameRef.current?.select()
            })
          }
        >
          <PencilSimple size={16} />
          Rename…
        </DropdownMenuItem>
        {folder.parentId === null && (
          <DropdownMenuItem disabled={busy || disabledReason !== undefined} onSelect={newSubfolder}>
            <FolderPlus size={16} />
            New subfolder
          </DropdownMenuItem>
        )}
        <DropdownMenuItem disabled={busy} onSelect={duplicate}>
          <Copy size={16} />
          Duplicate folder
        </DropdownMenuItem>
        {folder.ownDeactivated ? (
          <DropdownMenuItem disabled={busy} onSelect={() => setActive(true)}>
            <ArrowCounterClockwise size={16} />
            Restore folder
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem disabled={busy || folder.deactivated} onSelect={() => setActive(false)}>
            <Archive size={16} />
            Archive folder
          </DropdownMenuItem>
        )}
        {deleting ? (
          <DropdownMenuItem
            disabled={busy}
            onSelect={() => draft.unmarkForDeletion({ folderId: folder.id })}
          >
            <ArrowCounterClockwise size={16} />
            Keep folder
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem disabled={busy} onSelect={onDelete} className="text-fg-negative-text">
            <Trash size={16} />
            Delete folder…
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function FolderDatesCard({
  folder,
  parent,
  disabledReason,
  save,
}: {
  folder: PackageFolder
  parent: PackageFolder | undefined
  disabledReason: string | undefined
  save: SaveFolder
}) {
  const { allowed } = usePermission("edit")
  const following = parent !== undefined && folder.ownStartDate === null
  const current =
    folder.startDate && folder.cutoffDate
      ? { startDate: folder.startDate, cutoffDate: folder.cutoffDate }
      : null
  const commit = (window: { startDate: string; cutoffDate: string } | null) =>
    save.mutate(
      { op: { kind: "setFolderWindow", folderId: folder.id, window } },
      { onSuccess: (result) => noteRunAfterCurrent(result.run), onError: showErrorToast },
    )
  const locked = !allowed || disabledReason !== undefined || save.isPending
  return (
    <ExpandableCard
      className="shrink-0"
      persistKey={`${PACKAGE_CARD_KEY}.folder-dates`}
      defaultExpanded
    >
      <ExpandableCardHeader>
        <CalendarBlank size={16} className="text-fg-muted" />
        <CardTitle meta={packageWindowLabel(folder.startDate, folder.cutoffDate)}>Dates</CardTitle>
        {parent && (
          <Badge variant={following ? "neutral" : "violet"}>
            {following ? `From ${parent.name}` : "Own dates"}
          </Badge>
        )}
      </ExpandableCardHeader>
      <ExpandableCardContent className="flex flex-col gap-3">
        <div className="flex items-center gap-3">
          <PermissionTooltip requires="edit" allowedTitle={disabledReason}>
            <div className="w-[280px]">
              <DateRangePicker
                startDate={current ? toDate(current.startDate) : undefined}
                endDate={current ? toDate(current.cutoffDate) : undefined}
                onApply={({ from, to }) => {
                  if (!from || !to) return
                  commit({ startDate: toUTCDateString(from), cutoffDate: toUTCDateString(to) })
                }}
                formatDate={dateFieldLabel}
                validateRange={(from, to) =>
                  from && to ? null : "Pick a start date and a replenishment cut-off."
                }
                align="start"
                disabled={locked}
              />
            </div>
          </PermissionTooltip>
          {parent && !following && (
            <Button
              variant="transparent"
              size="sm"
              requires="edit"
              allowedTitle={disabledReason ?? `Follow ${parent.name}'s dates again`}
              disabled={locked}
              onClick={() => commit(null)}
            >
              {`Use ${parent.name}'s dates`}
            </Button>
          )}
          {!parent && current && (
            <Button
              variant="transparent"
              size="sm"
              requires="edit"
              allowedTitle={disabledReason ?? "Packages that follow this folder stop replenishing"}
              disabled={locked}
              onClick={() => commit(null)}
            >
              Clear dates
            </Button>
          )}
        </div>
        <p className="paragraph-sm text-fg-muted">
          Seasonal and clearance packages without dates of their own follow these, and stop
          replenishing until this folder has dates. NOOS packages stay always open.
        </p>
      </ExpandableCardContent>
    </ExpandableCard>
  )
}

function FolderStoresCard({
  folder,
  parent,
  packageCount,
  disabledReason,
  save,
}: {
  folder: PackageFolder
  parent: PackageFolder | undefined
  packageCount: number
  disabledReason: string | undefined
  save: SaveFolder
}) {
  const [picked, setPicked] = useState<Set<string> | null>(null)
  const saved = new Set(folder.storeIds)
  const assignedIds = picked ?? saved
  const following = parent !== undefined && !folder.ownStores
  const changed =
    picked !== null &&
    (picked.size !== saved.size || [...picked].some((storeId) => !saved.has(storeId)))
  const apply = () =>
    save.mutate(
      { op: { kind: "setFolderStores", folderId: folder.id, storeIds: [...assignedIds] } },
      {
        onSuccess: (result) => {
          setPicked(null)
          noteRunAfterCurrent(result.run)
        },
        onError: showErrorToast,
      },
    )
  const setOwn = (ownStores: boolean) =>
    save.mutate(
      { op: { kind: "setFolderStoresSource", folderId: folder.id, ownStores } },
      { onSuccess: (result) => noteRunAfterCurrent(result.run), onError: showErrorToast },
    )
  return (
    <FilterScopeProvider scopeKey={STORES_SCOPE}>
      <StoresCardBody
        persistKey={`${PACKAGE_CARD_KEY}.folder-stores`}
        assignedIds={assignedIds}
        isSaved={(storeId) => saved.has(storeId)}
        onAssign={(storeIds, checked) => {
          const next = new Set(assignedIds)
          for (const storeId of storeIds) {
            if (checked) next.add(storeId)
            else next.delete(storeId)
          }
          setPicked(next)
        }}
        disabledReason={disabledReason}
        targetLabel="every package in this folder"
        emptyTitle="This folder has no stores yet"
        source={
          parent && (
            <StoresSource
              followingLabel={following ? `From ${parent.name}` : undefined}
              actionLabel={following ? "Use own stores" : `Use ${parent.name}'s stores`}
              actionTitle={
                following
                  ? "Give this subfolder its own store list, starting from its parent's"
                  : `Follow ${parent.name}'s stores again`
              }
              disabled={disabledReason !== undefined || save.isPending}
              onAction={() => setOwn(following)}
            />
          )
        }
        footer={
          <InlineAlert
            tone={changed ? "warning" : "neutral"}
            title={`Stores apply to all ${pluralize(packageCount, "package")} in this folder`}
            description="Every package and subfolder inside ends up with exactly these stores, including those with their own store list. Takes effect immediately."
            secondaryAction={
              changed ? { label: "Reset", onClick: () => setPicked(null) } : undefined
            }
            action={{
              label: `Apply to all ${pluralize(packageCount, "package")}`,
              onClick: apply,
              requires: "edit",
              disabled: !changed || save.isPending || disabledReason !== undefined,
            }}
          />
        }
      />
    </FilterScopeProvider>
  )
}

function FolderPackagesCard({
  packages,
  isDeleting,
  onSelectPackage,
}: {
  packages: RangePackage[]
  isDeleting: (packageId: string) => boolean
  onSelectPackage: (packageId: string) => void
}) {
  return (
    <ExpandableCard
      className="shrink-0"
      persistKey={`${PACKAGE_CARD_KEY}.folder-packages`}
      defaultExpanded
    >
      <ExpandableCardHeader>
        <Package size={16} className="text-fg-muted" />
        <CardTitle meta={pluralize(packages.length, "package")}>Packages</CardTitle>
      </ExpandableCardHeader>
      <ExpandableCardContent>
        {packages.length === 0 ? (
          <EmptyState
            icon={Package}
            title="No packages in this folder"
            description="Drag a package here from the list, or create one. An empty folder is fine."
            className="py-6"
          />
        ) : (
          <div className="flex flex-col">
            {packages.map((pkg) => (
              <Button
                key={pkg.id}
                variant="transparent"
                className="justify-start [&>div]:w-full [&>div]:justify-start [&>div]:gap-2"
                onClick={() => onSelectPackage(pkg.id)}
              >
                <span className="flex-1 truncate text-left">{pkg.name}</span>
                {isDeleting(pkg.id) && (
                  <span className="paragraph-xs text-fg-muted">· deleting on save</span>
                )}
                <Badge variant={PACKAGE_STATUS_META[pkg.status].variant}>
                  {PACKAGE_STATUS_META[pkg.status].label}
                </Badge>
              </Button>
            ))}
          </div>
        )}
      </ExpandableCardContent>
    </ExpandableCard>
  )
}

export function PackageFolderPage({
  folder,
  folders,
  packages,
  onSelectFolder,
  onSelectPackage,
}: {
  folder: PackageFolder
  folders: PackageFolder[]
  packages: RangePackage[]
  onSelectFolder: (folderId: string) => void
  onSelectPackage: (packageId: string) => void
}) {
  const { savePackageFolder } = useAllocationMutations()
  const draft = usePackageStoresDraft()
  const nameRef = useRef<HTMLInputElement>(null)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const parent = folders.find((other) => other.id === folder.parentId)
  const childIds = new Set([
    folder.id,
    ...folders.filter((other) => other.parentId === folder.id).map((other) => other.id),
  ])
  const inside = packages.filter((pkg) => pkg.folderId !== null && childIds.has(pkg.folderId))
  const deleting = draft.isFolderDeleting(folder.id)
  const disabledReason = folderDisabledReason(folder, deleting)
  return (
    <div className="flex flex-1 flex-col gap-4 overflow-y-auto p-6">
      {parent && (
        <Button
          variant="transparent"
          size="sm"
          className="self-start"
          onClick={() => onSelectFolder(parent.id)}
        >
          {parent.name} ›
        </Button>
      )}
      <div className="flex items-center gap-3">
        <FolderName
          key={`${folder.id}:${folder.name}`}
          folder={folder}
          siblings={folders.filter((other) => other.parentId === folder.parentId)}
          nameRef={nameRef}
          disabledReason={disabledReason}
          save={savePackageFolder}
        />
        {folder.deactivated && <Badge variant="neutral">Archived</Badge>}
        {deleting && <span className="label-sm text-fg-muted">· deleting on save</span>}
        <span className="label-sm text-fg-muted">{pluralize(inside.length, "package")}</span>
        <div className="flex-1" />
        <FolderActionsMenu
          folder={folder}
          folders={folders}
          nameRef={nameRef}
          disabledReason={disabledReason}
          save={savePackageFolder}
          deleting={deleting}
          onSelectFolder={onSelectFolder}
          onDelete={() => setConfirmDelete(true)}
        />
      </div>
      {confirmDelete && !deleting && (
        <DeletionImpact
          target={{ folderId: folder.id }}
          label={`${folder.name} and everything in it`}
          onCancel={() => setConfirmDelete(false)}
          onMarked={() => setConfirmDelete(false)}
        />
      )}
      <FolderDatesCard
        folder={folder}
        parent={parent}
        disabledReason={disabledReason}
        save={savePackageFolder}
      />
      <FolderStoresCard
        folder={folder}
        parent={parent}
        packageCount={inside.length}
        disabledReason={disabledReason}
        save={savePackageFolder}
      />
      <FolderPackagesCard
        packages={inside}
        isDeleting={draft.isDeleting}
        onSelectPackage={onSelectPackage}
      />
    </div>
  )
}
